<!-- Единая карточка действия на главной странице. -->
<template>
  <div
    class="quick-action-card"
  >
    <button
      class="quick-action-card__main"
      type="button"
      @click="$emit('click')"
    >
      <span
        class="quick-action-card__icon"
        :class="`quick-action-card__icon--${color}`"
        :style="iconStyle"
      >
        <AppIcon
          :name="iconName"
          :size="18"
        />
      </span>

      <span class="quick-action-card__content">
        <span class="quick-action-card__label">{{ label }}</span>
        <span v-if="description" class="quick-action-card__description">
          {{ description }}
        </span>
      </span>

      <AppIcon
        v-if="withArrow && !listTo"
        class="quick-action-card__arrow"
        name="chevronRight"
        :size="14"
      />
    </button>

    <BaseTooltip
      v-if="listTo"
      :text="listLabel"
      position="top"
    >
      <button
        class="quick-action-card__list-button"
        type="button"
        :aria-label="listLabel"
        @click.stop="openList"
      >
        <AppIcon
          name="externalLink"
          :size="18"
        />
        <span class="quick-action-card__list-label">
          <span class="quick-action-card__list-action">
            {{ listActionText }}
          </span>
          <span
            v-if="listSubject"
            class="quick-action-card__list-subject"
          >
            {{ listSubject }}
          </span>
        </span>
      </button>
    </BaseTooltip>
  </div>
</template>

<script>
import AppIcon from "@/components/Icon/AppIcon.vue";

export default {
  name: "HomeQuickActionCard",

  components: {
    AppIcon,
  },

  props: {
    label: {
      type: String,
      required: true,
    },
    description: {
      type: String,
      default: "",
    },
    color: {
      type: String,
      required: true,
    },
    iconBackground: {
      type: String,
      default: "",
    },
    iconName: {
      type: String,
      required: true,
    },
    withArrow: {
      type: Boolean,
      default: true,
    },
    listTo: {
      type: [Object, String],
      default: null,
    },
    listLabel: {
      type: String,
      default: "Открыть список",
    },
    listActionText: {
      type: String,
      default: "Открыть",
    },
    listSubject: {
      type: String,
      default: "",
    },
  },

  emits: ["click"],

  computed: {
    iconStyle() {
      if (!this.iconBackground) return null;

      return { background: this.iconBackground };
    },
  },

  methods: {
    openList() {
      if (!this.listTo) return;

      this.$router.push(this.listTo);
    },
  },
};
</script>

<style scoped>
.quick-action-card {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 64px;
  border: 1px solid var(--color-grey, #e5e7eb);
  border-radius: 10px;
  background: #fff;
  text-align: left;
  overflow: hidden;
  transition: box-shadow 0.18s, border-color 0.18s, background 0.18s;
}

.quick-action-card:hover {
  background: #fafafa;
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.quick-action-card:active {
  background: #f3f4f6;
  box-shadow: none;
}

.quick-action-card__main {
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
  min-height: 62px;
  padding: 12px 14px 12px 14px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}

.quick-action-card__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 9px;
  color: #fff;
}

.quick-action-card__icon--indigo {
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
}

.quick-action-card__icon--blue {
  background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
}

.quick-action-card__icon--cyan {
  background: linear-gradient(135deg, #0891b2 0%, #06b6d4 100%);
}

.quick-action-card__icon--teal {
  background: linear-gradient(135deg, #16a34a 0%, #84cc16 100%);
}

.quick-action-card__icon--violet {
  background: linear-gradient(135deg, #0ea5e9 0%, #10b981 100%);
}

.quick-action-card__icon--gray {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
}

.quick-action-card__icon--amber {
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
}

.quick-action-card__icon--green {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
}

.quick-action-card__icon--red {
  background: #dc2626;
}

.quick-action-card__content {
  display: grid;
  gap: 2px;
  flex: 1;
  min-width: 0;
}

.quick-action-card__label {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-dark, #1e1e2f);
  line-height: 1.2;
}

.quick-action-card__description {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.35;
}

.quick-action-card__arrow {
  flex-shrink: 0;
  color: #666;
}

.quick-action-card__list-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  flex-shrink: 0;
  align-self: stretch;
  min-width: 64px;
  padding: 0 8px;
  border: 0;
  border-left: 1px solid #eef0f3;
  border-radius: 0;
  background: transparent;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.18s, color 0.18s;
}

.quick-action-card__list-button:hover {
  background: #f3f4f6;
  color: var(--color-dark, #1e1e2f);
}

.quick-action-card__list-label {
  display: grid;
  gap: 1px;
  justify-items: center;
  line-height: 1.05;
  white-space: nowrap;
}

.quick-action-card__list-action,
.quick-action-card__list-subject {
  font-size: 9px;
  font-weight: 500;
  color: currentColor;
}

.quick-action-card__main:focus-visible,
.quick-action-card__list-button:focus-visible {
  outline: 2px solid #93c5fd;
  outline-offset: -2px;
}

@media (max-width: 600px) {
  .quick-action-card {
    min-height: 62px;
    gap: 6px;
  }

  .quick-action-card__main {
    min-height: 60px;
    padding: 11px 12px;
    gap: 12px;
  }

  .quick-action-card__icon {
    width: 34px;
    height: 34px;
    border-radius: 8px;
  }

  .quick-action-card__label {
    font-size: 13px;
  }

  .quick-action-card__list-button {
    min-width: 62px;
    padding: 0 7px;
  }
}
</style>
